import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Brand, Branch, Membership, TaskCategory } from '@/domain/types';
import { useAuth } from './auth';
import { listBrands, listBranches, listCategories, listMembershipDirectory } from '@/services/org-service';

export interface OrgLookups {
  branches: Branch[];
  brands: Brand[];
  categories: TaskCategory[];
  members: Membership[];
  memberNames: Map<string, string>;
  memberAvatars: Map<string, string>;
  branchNames: Map<string, string>;
  brandNames: Map<string, string>;
  categoryNames: Map<string, string>;
  loading: boolean;
  reload: () => Promise<void>;
}

interface CachedLookupsData {
  branches: Branch[];
  brands: Brand[];
  categories: TaskCategory[];
  members: Membership[];
  memberNames: Map<string, string>;
  memberAvatars: Map<string, string>;
  branchNames: Map<string, string>;
  brandNames: Map<string, string>;
  categoryNames: Map<string, string>;
  fetchedAt: number;
}

const lookupsCache = new Map<string, CachedLookupsData>();
const lookupsInFlight = new Map<string, Promise<CachedLookupsData>>();

export async function fetchOrgLookupsData(gateway: any, companyId: string): Promise<CachedLookupsData> {
  const inFlight = lookupsInFlight.get(companyId);
  if (inFlight) return inFlight;

  const promise = (async () => {
    try {
      const [nextBrands, nextBranches, nextCategories, directory, profiles] = await Promise.all([
        listBrands(gateway, companyId),
        listBranches(gateway, companyId),
        listCategories(gateway, companyId),
        listMembershipDirectory(gateway, companyId),
        gateway.commands.listCompanyProfiles({ companyId }).catch(() => []),
      ]);
      const profileByUser = new Map((profiles as any[]).map((profile) => [profile.userId, profile]));
      const memberNames = new Map(
        directory.map((entry) => {
          const profile = profileByUser.get(entry.membership.userId);
          return [
            entry.membership.id,
            profile?.fullName ||
              entry.profile?.fullName ||
              entry.profile?.email ||
              entry.membership.id.slice(0, 8),
          ];
        }),
      );
      const memberAvatars = new Map(
        directory
          .map((entry) => {
            const avatar =
              profileByUser.get(entry.membership.userId)?.avatarDataUrl ??
              entry.profile?.avatarDataUrl ??
              null;
            return [entry.membership.id, avatar] as const;
          })
          .filter((entry): entry is readonly [string, string] => Boolean(entry[1])),
      );

      const cached: CachedLookupsData = {
        branches: nextBranches,
        brands: nextBrands,
        categories: nextCategories,
        members: directory.map((entry) => entry.membership),
        memberNames,
        memberAvatars,
        branchNames: new Map(nextBranches.map((branch) => [branch.id, branch.name])),
        brandNames: new Map(nextBrands.map((brand) => [brand.id, brand.name])),
        categoryNames: new Map(nextCategories.map((category) => [category.id, category.name])),
        fetchedAt: Date.now(),
      };
      lookupsCache.set(companyId, cached);
      return cached;
    } finally {
      lookupsInFlight.delete(companyId);
    }
  })();

  lookupsInFlight.set(companyId, promise);
  return promise;
}

export function invalidateOrgLookups(companyId?: string) {
  if (companyId) {
    lookupsCache.delete(companyId);
  } else {
    lookupsCache.clear();
  }
}

export function preloadOrgLookups(gateway: any, companyId: string) {
  if (!companyId) return;
  void fetchOrgLookupsData(gateway, companyId);
}

/** بيانات مرجعية مشتركة للواجهة (فروع/علامات/فئات/أعضاء) داخل الشركة النشطة. */
export function useOrgLookups(): OrgLookups {
  const { gateway, snapshot } = useAuth();
  const companyId = snapshot?.activeCompanyId ?? null;

  const [data, setData] = useState<CachedLookupsData>(() => {
    if (companyId && lookupsCache.has(companyId)) {
      return lookupsCache.get(companyId)!;
    }
    return {
      branches: [],
      brands: [],
      categories: [],
      members: [],
      memberNames: new Map(),
      memberAvatars: new Map(),
      branchNames: new Map(),
      brandNames: new Map(),
      categoryNames: new Map(),
      fetchedAt: 0,
    };
  });

  const [loading, setLoading] = useState<boolean>(() => {
    return Boolean(companyId && !lookupsCache.has(companyId));
  });

  const reload = useCallback(async () => {
    if (!companyId) return;
    if (!lookupsCache.has(companyId)) {
      setLoading(true);
    }
    try {
      const fresh = await fetchOrgLookupsData(gateway, companyId);
      setData(fresh);
    } catch {
      // الصفحة تعرض حالة فارغة أو تحتفظ بالبيانات السابقة
    } finally {
      setLoading(false);
    }
  }, [companyId, gateway]);

  useEffect(() => {
    if (!companyId) return;
    const cached = lookupsCache.get(companyId);
    if (cached) {
      setData(cached);
      // Revalidate in background if older than 45 seconds (SWR)
      if (Date.now() - cached.fetchedAt > 45000) {
        void fetchOrgLookupsData(gateway, companyId).then((fresh) => {
          setData(fresh);
        });
      }
    } else {
      void reload();
    }
  }, [companyId, gateway, reload]);

  return useMemo(
    () => ({
      branches: data.branches,
      brands: data.brands,
      categories: data.categories,
      members: data.members,
      memberNames: data.memberNames,
      memberAvatars: data.memberAvatars,
      branchNames: data.branchNames,
      brandNames: data.brandNames,
      categoryNames: data.categoryNames,
      loading,
      reload,
    }),
    [data, loading, reload],
  );
}
